<template>
    <div v-if="visible" class="date-picker-popup">
        <!-- Month View -->
        <div v-if="currentView === 'month'" class="date-picker-content">
            <!-- Year Header with Navigation -->
            <div class="date-picker-header">
                <button class="nav-btn" @click="changeYear(-1)">
                    <i class="fa-solid fa-chevron-up"></i>
                </button>
                <div class="year-title" @click="switchToYearView">{{ currentYear }}年</div>
                <button class="nav-btn" @click="changeYear(1)">
                    <i class="fa-solid fa-chevron-down"></i>
                </button>
            </div>

            <!-- Month Grid -->
            <div class="month-grid">
                <div
                    v-for="month in 12"
                    :key="month"
                    :class="{
                        'month-item': true,
                        'current-month': month === currentMonth,
                        'selected-month': month === selectedMonth && currentYear === selectedYear
                    }"
                    @click="selectMonth(month)"
                >
                    {{ month }}
                </div>
            </div>
        </div>

        <!-- Year View -->
        <div v-if="currentView === 'year'" class="date-picker-content">
            <!-- Year Range Header with Navigation -->
            <div class="date-picker-header">
                <button class="nav-btn" @click="changeYearRange(-10)">
                    <i class="fa-solid fa-chevron-up"></i>
                </button>
                <div class="year-range-title">{{ yearRangeStart }} - {{ yearRangeEnd }}</div>
                <button class="nav-btn" @click="changeYearRange(10)">
                    <i class="fa-solid fa-chevron-down"></i>
                </button>
            </div>

            <!-- Year Grid -->
            <div class="year-grid">
                <div
                    v-for="year in yearRange"
                    :key="year"
                    :class="{
                        'year-item': true,
                        'current-year': year === new Date().getFullYear(),
                        'selected-year': year === selectedYear,
                        'disabled-year': year < yearRangeStart || year > yearRangeEnd
                    }"
                    @click="selectYear(year)"
                >
                    {{ year }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * ****************************************************************************
 * @description     :   Date Picker Popup Component
 * @created at      :   2025/09/29
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

export default {
    name: 'DatePickerPopup',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        initialDate: {
            type: Date,
            default: () => new Date()
        }
    },
    emits: ['close', 'select'],
    data() {
        return {
            currentView: 'month', // 'month' or 'year'
            currentYear: new Date().getFullYear(),
            currentMonth: new Date().getMonth() + 1,
            selectedYear: new Date().getFullYear(),
            selectedMonth: new Date().getMonth() + 1,
            yearRangeStart: 2020,
            yearRangeEnd: 2029
        };
    },
    computed: {
        yearRange() {
            const years = [];
            for (let year = this.yearRangeStart; year <= this.yearRangeEnd; year++) {
                years.push(year);
            }
            return years;
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.initializePicker();
            }
        },
        initialDate: {
            handler(newDate) {
                if (newDate) {
                    this.currentYear = newDate.getFullYear();
                    this.currentMonth = newDate.getMonth() + 1;
                    this.selectedYear = newDate.getFullYear();
                    this.selectedMonth = newDate.getMonth() + 1;
                    this.updateYearRange(newDate.getFullYear());
                }
            },
            immediate: true
        }
    },
    methods: {
        initializePicker() {
            this.currentView = 'month';
            const date = this.initialDate || new Date();
            this.currentYear = date.getFullYear();
            this.currentMonth = date.getMonth() + 1;
            this.selectedYear = date.getFullYear();
            this.selectedMonth = date.getMonth() + 1;
            this.updateYearRange(date.getFullYear());
        },

        closePopup() {
            this.$emit('close');
        },

        changeYear(direction) {
            this.currentYear += direction;
            this.updateYearRange(this.currentYear);
        },

        changeYearRange(direction) {
            this.yearRangeStart += direction;
            this.yearRangeEnd += direction;
        },

        updateYearRange(centerYear) {
            // Center the year range around the current year
            const startYear = Math.floor(centerYear / 10) * 10;
            this.yearRangeStart = startYear;
            this.yearRangeEnd = startYear + 9;
        },

        switchToYearView() {
            this.currentView = 'year';
            this.updateYearRange(this.currentYear);
        },

        selectMonth(month) {
            this.selectedMonth = month;
            const selectedDate = new Date(this.currentYear, month - 1, this.initialDate.getDate());
            this.$emit('select', selectedDate);
            this.closePopup();
        },

        selectYear(year) {
            if (year < this.yearRangeStart || year > this.yearRangeEnd) return;

            this.selectedYear = year;
            this.currentYear = year;
            this.currentView = 'month';
        }
    }
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/common/var' as *;

.date-picker-popup {
    background: #2d3748;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    padding: 16px;
    min-width: 280px;
    max-width: 320px;
}

.date-picker-content {
    color: white;
}

.date-picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 0 8px;

    .nav-btn {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 14px;

        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }

        i {
            font-size: 12px;
        }
    }

    .year-title,
    .year-range-title {
        font-weight: bold;
        font-size: 16px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;

        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }
    }

    .year-range-title {
        cursor: default;

        &:hover {
            background-color: transparent;
        }
    }
}

.month-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;

    .month-item {
        padding: 12px 8px;
        text-align: center;
        cursor: pointer;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }

        &.current-month {
            background-color: #4299e1;
            color: white;
        }

        &.selected-month {
            background-color: #3182ce;
            color: white;
            font-weight: bold;
        }
    }
}

.year-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;

    .year-item {
        padding: 12px 8px;
        text-align: center;
        cursor: pointer;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s ease;

        &:hover:not(.disabled-year) {
            background-color: rgba(255, 255, 255, 0.1);
        }

        &.current-year {
            background-color: #4299e1;
            color: white;
        }

        &.selected-year {
            background-color: #3182ce;
            color: white;
            font-weight: bold;
        }

        &.disabled-year {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }
}
</style>
